import { get, put, post, del } from './requests.js'

// Query string shared by the per-field organization config endpoints.
// changeGroup correlates the separate requests of a single multi-field save so the
// audit log can render them as one entry in the history view.
const orgQuery = ({ env, changeGroup }) =>
  `?env=${env}` + (changeGroup ? `&change_group=${encodeURIComponent(changeGroup)}` : '')

export const getOrganizations = (env) =>
  get(
    [
      `organizations-apa?env=${env}&fields=destinations&fields=data_sources&fields=created_by&fields=name&fields=features&fields=audience_features&fields=dataset_limit&fields=taxonomy_features&fields=source_partners&fields=audience_insights&fields=purchase_based_data&fields=disable_audience_builder&fields=hide_pending_data_insights&fields=online_media_measurement&fields=omm_destinations&fields=url_audience&fields=enable_revenue_report`,
    ],
    {
      removeTrailingSlash: true,
    }
  )

export const getApaUsers = (env, params = {}) =>
  get([`users/apa`], { removeTrailingSlash: true }, { env, ...params })

export const getUsers = (organizationId, env) =>
  get([`organizations-apa/${organizationId}/users?env=${env}&fields=email`], {
    removeTrailingSlash: true,
  })

export const getDeactivatedUsers = (organizationId, env) =>
  get([`organizations-apa/${organizationId}/deactivated-users?env=${env}&fields=email`], {
    removeTrailingSlash: true,
  })

export const createOrganization = ({ env, name, changeGroup }) =>
  post(
    [`organizations-apa${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { name }
  )

export const getFeatures = (env) => get([`features-apa?env=${env}`], { removeTrailingSlash: true })

export const createFeature = ({ env, name, label, type }) =>
  post([`features-apa?env=${env}`], { removeTrailingSlash: true }, { name, label, type })

export const deleteFeature = ({ env, id }) =>
  del([`features-apa/${id}?env=${env}`], { removeTrailingSlash: true })

export const getOrgPendingInvitations = ({ env, organizationId }) =>
  get([`invitations-apa/pending?env=${env}&organization_id=${organizationId}`], {
    removeTrailingSlash: true,
  })

export const getOrganizationUsers = ({ env, id }) =>
  get([`organizations-apa/${id}/users?env=${env}`], {
    removeTrailingSlash: true,
  })

export const getOrganizationDeactivatedUsers = (organizationId, env) =>
  get([`organizations-apa/${organizationId}/deactivated-users?env=${env}&fields=email`], {
    removeTrailingSlash: true,
  })

export const removeOrganizationUser = ({ env, organizationId, userId, email }) =>
  del(
    [
      `organizations-apa/${organizationId}/users/${userId}?env=${env}` +
        (email ? `&email=${encodeURIComponent(email)}` : ''),
    ],
    {
      removeTrailingSlash: true,
    }
  )

export const inviteUser = ({ env, invitedUser, organizationId, role }) =>
  post(
    [`invitations-apa?env=${env}`],
    { removeTrailingSlash: true },
    { invitedUser, organizationId, role }
  )

export const deleteOrgPendingInvitation = ({ env, id, organizationId, invitedUser }) =>
  del(
    [
      `invitations-apa/${id}?env=${env}` +
        (organizationId ? `&organization_id=${organizationId}` : '') +
        (invitedUser ? `&invited_user=${encodeURIComponent(invitedUser)}` : ''),
    ],
    { removeTrailingSlash: true }
  )

export const updateFeatures = ({ id, env, features, changeGroup }) =>
  put(
    [`organizations-apa/feature-toggle/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { features }
  )

export const getEnterpriseRequests = (env) =>
  get([`organizations-apa/enterprise_requests?env=${env}`], {
    removeTrailingSlash: true,
    transformPayload: false,
  })

export const enterpriseResendEmail = ({ env, id }) =>
  put([`organizations-apa/enterprise_requests/resend-email?env=${env}&id=${id}`], {
    removeTrailingSlash: true,
  })

export const getFreeUserRequests = (env) =>
  get([`organizations-apa/free_trial_requests?env=${env}`], {
    removeTrailingSlash: true,
    transformPayload: false,
  })

export const freeUserApprove = ({ env, id }) =>
  put([`organizations-apa/free_trial_requests/accept?env=${env}&id=${id}`], {
    removeTrailingSlash: true,
  })

export const freeUserDeny = ({ env, id }) =>
  put([`organizations-apa/free_trial_requests/reject?env=${env}&id=${id}`], {
    removeTrailingSlash: true,
  })

export const setAPADataSources = ({ data_sources, env, id, changeGroup }) =>
  put(
    [`organizations-apa/data-sources/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { data_sources }
  )

// PDX Databricks clean rooms. One per organization; the POST starts a Databricks
// job and returns immediately with status "creating", and the GET settles that
// status by asking Databricks how the run ended.
export const createCleanroom = ({ env, id, sharingIdentifier, hasPii, changeGroup }) =>
  post(
    [`organizations-apa/cleanroom/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { sharing_identifier: sharingIdentifier, has_pii: Boolean(hasPii) }
  )

export const getCleanroom = ({ env, id }) =>
  get([`organizations-apa/cleanroom/${id}?env=${env}`], { removeTrailingSlash: true })

export const setAPADestinations = ({ destinations, env, id, changeGroup }) =>
  put(
    [`organizations-apa/destinations/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { destinations }
  )

export const setAudienceFeatures = ({ env, id, audienceFeatures, changeGroup }) =>
  put(
    [`organizations-apa/audience-features/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { audienceFeatures }
  )

export const setTaxonomyFeatures = ({ env, id, taxonomy_features, changeGroup }) =>
  put(
    [`organizations-apa/taxonomy-features/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { taxonomy_features }
  )

export const setOrgDatasetsLimit = ({ env, id, datasetLimit, changeGroup }) =>
  put(
    [`organizations-apa/dataset-limit/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { limit: datasetLimit }
  )

export const getOrgRateLimits = ({ env, id }) =>
  get([`organizations-apa/rate-limit/${id}?env=${env}`], { removeTrailingSlash: true })

export const setOrgRateLimit = ({ env, id, limit, apiType, changeGroup }) =>
  put(
    [`organizations-apa/rate-limit/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { limit, apiType }
  )

export const getPublicTaxonomies = (env) =>
  get([`organizations-apa/public-taxonomies?env=${env}`], {
    removeTrailingSlash: true,
    transformPayload: false,
  })

export const setAudienceInsights = ({ env, id, audienceInsights, changeGroup }) =>
  put(
    [`organizations-apa/audience-insights/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { audienceInsights }
  )

export const setPurchaseBasedData = ({ env, id, purchaseBasedData, changeGroup }) =>
  put(
    [`organizations-apa/purchase-based-data/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { purchaseBasedData }
  )

export const setDisableAudienceBuilder = ({ env, id, disableAudienceBuilder, changeGroup }) =>
  put(
    [`organizations-apa/disable-audience-builder/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { disableAudienceBuilder }
  )

export const setOnlineMediaMeasurement = ({ env, id, onlineMediaMeasurement, changeGroup }) =>
  put(
    [`organizations-apa/online-media-measurement/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { onlineMediaMeasurement }
  )

export const setOmmDestinations = ({ env, id, ommDestinations, changeGroup }) =>
  put(
    [`organizations-apa/omm-destinations/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { ommDestinations }
  )

export const setUrlAudience = ({ env, id, urlAudience, changeGroup }) =>
  put(
    [`organizations-apa/url-audience/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { urlAudience }
  )

export const setEnableRevenueReport = ({ env, id, enableRevenueReport, changeGroup }) =>
  put(
    [`organizations-apa/enable-revenue-report/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { enableRevenueReport }
  )

export const setHidePendingDataInsights = ({ env, id, hidePendingDataInsights, changeGroup }) =>
  put(
    [`organizations-apa/hide-pending-data-insights/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { hidePendingDataInsights }
  )

export const addSourcePartnersToOrg = (env, id, body, changeGroup) =>
  put(
    [`organizations-apa/${id}/source-partners${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true, transformPayload: true },
    { partners: body }
  )

export const setOrgActive = ({ env, id, active, changeGroup }) =>
  put(
    [`organizations-apa/active/${id}${orgQuery({ env, changeGroup })}`],
    { removeTrailingSlash: true },
    { active }
  )

// History of config updates for an organization (newest first).
export const getOrganizationAuditLog = ({ env, id }) =>
  get([`organizations-apa/${id}/audit-log?env=${env}`], { removeTrailingSlash: true })
